import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactPlayer from "react-player";
import { videoService } from "../../services/videoService";
import { Video } from "../../types";
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ClockIcon,
  VideoCameraIcon,
  SparklesIcon,
  BookOpenIcon,
} from "@heroicons/react/24/outline";

const VideoPlayer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const playerRef = useRef<ReactPlayer>(null);

  const [video, setVideo] = useState<Video | null>(null);
  const [streamUrl, setStreamUrl] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [watchedDuration, setWatchedDuration] = useState(0);
  const [hasCompleted, setHasCompleted] = useState(false);
  const [metadataLoaded, setMetadataLoaded] = useState(false);
  const [viewCounted, setViewCounted] = useState(false);
  const [viewTrackingStarted, setViewTrackingStarted] = useState(false);
  const [viewCountAttempted, setViewCountAttempted] = useState(false);

  useEffect(() => {
    if (id) {
      fetchVideo(parseInt(id));
    }
  }, [id]);

  const fetchVideo = async (videoId: number) => {
    try {
      const videoData = await videoService.getVideo(videoId);
      setVideo(videoData.video);
      setDuration(videoData.video.duration || 60);
      setWatchedDuration(videoData.video.watched_duration || 0);
      setHasCompleted(videoData.video.completed || false);

      const streamData = await videoService.getVideoStreamUrl(videoId);
      setStreamUrl(streamData.streamUrl);
    } catch (error) {
      console.error("Error fetching video:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProgress = (state: { played: number; playedSeconds: number }) => {
    const validDuration = duration || playerRef.current?.getDuration() || 1;
    setProgress((state.playedSeconds / validDuration) * 100);

    const currentWatchedDuration = Math.floor(state.playedSeconds);
    if (currentWatchedDuration > watchedDuration) {
      setWatchedDuration(currentWatchedDuration);
    }

    // Check for view counting (YouTube-like)
    if (viewTrackingStarted && !viewCounted && currentWatchedDuration > 0) {
      handleViewCount();
    }

    // Check for completion (90% or more watched)
    const completionThreshold = 0.9;
    const progressRatio = state.playedSeconds / validDuration;

    if (progressRatio >= completionThreshold && !hasCompleted) {
      console.log("🎉 Video completion triggered:", {
        playedSeconds: state.playedSeconds,
        validDuration,
        progressRatio,
        completionThreshold,
      });
      setHasCompleted(true);
      updateProgress(true);
    }
  };

  const updateProgress = async (completed: boolean = false) => {
    if (!video) return;

    try {
      console.log("🔄 Updating progress:", {
        videoId: video.id,
        watchedDuration,
        completed,
      });

      const result = await videoService.updateProgress(video.id, {
        watchedDuration,
        completed,
      });

      console.log("✅ Progress updated successfully:", result);
    } catch (error) {
      console.error("❌ Error updating progress:", error);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    if (!hasCompleted) {
      setHasCompleted(true);
      updateProgress(true);
    }
  };

  const handleDuration = (duration: number) => {
    console.log("📏 Video duration detected:", duration);
    if (duration > 0) {
      setDuration(duration);
      setMetadataLoaded(true);
      console.log("✅ Duration set to:", duration);
    } else {
      console.log("⚠️ Invalid duration detected:", duration);
    }
  };

  const handleVideoStart = async () => {
    if (video && !viewTrackingStarted) {
      setViewTrackingStarted(true);
      console.log("Started tracking view for video:", video.id);
    }
  };

  const handleViewCount = async () => {
    if (
      video &&
      !viewCounted &&
      !viewCountAttempted &&
      duration > 0 &&
      viewTrackingStarted
    ) {
      setViewCountAttempted(true); // Prevent multiple attempts

      try {
        const minWatchPercentage = 0.5; // 50% of video duration
        const currentWatchPercentage = watchedDuration / duration;

        if (currentWatchPercentage >= minWatchPercentage) {
          const result = await videoService.updateViewsCount(
            video.id,
            watchedDuration,
            duration,
          );
          setViewCounted(true);
          console.log("View counted successfully:", result);
          console.log(
            "Watch percentage:",
            (currentWatchPercentage * 100).toFixed(1) + "%",
          );
        } else {
          console.log(
            "View not counted - insufficient watch percentage:",
            (currentWatchPercentage * 100).toFixed(1) + "%",
            "/",
            minWatchPercentage * 100 + "%",
          );
          setViewCountAttempted(false); // Allow retry if conditions change
        }
      } catch (error) {
        console.error("Error updating views count:", error);
        setViewCountAttempted(false); // Allow retry on error
      }
    }
  };

  const getBackNavigation = () => {
    // Check if user came from a folder page
    const referrer = document.referrer;
    if (referrer.includes("/folder/")) {
      const folderMatch = referrer.match(/\/folder\/([^\/]+)/);
      if (folderMatch) {
        // return `/folder/${folderMatch[1]}`; // This works but using video.folder_id is safer/cleaner if available
      }
    }

    // Check if video belongs to a folder
    if (video?.folder_id) {
      return `/folder/${video.folder_id}`;
    }

    // Default fallback to dashboard
    return "/dashboard";
  };

  useEffect(() => {
    return () => {
      if (watchedDuration > 0) {
        updateProgress(hasCompleted);
      }
    };
  }, [watchedDuration, hasCompleted]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">ກຳລັງໂຫຼດວິດີໂອ...</p>
        </div>
      </div>
    );
  }

  if (!video || !streamUrl) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-6">
            <VideoCameraIcon className="h-10 w-10 text-gray-400" />
          </div>
          <h3 className="text-2xl font-semibold text-gray-900 mb-3">
            ບໍ່ພົບວິດີໂອ
          </h3>
          <p className="text-gray-600 mb-8 max-w-md mx-auto">
            ບໍ່ສາມາດຊອກຫາວິດີໂອທີ່ທ່ານຕ້ອງການໄດ້
          </p>
          <button
            onClick={() => navigate(getBackNavigation())}
            className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors shadow-lg"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            ກັບຄືນ
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="w-full mx-auto px-2 sm:px-4 lg:px-6 py-4 sm:py-4 pb-24 sm:pb-4">
        {/* Header */}
        <div className="">
          <button
            onClick={() => navigate(getBackNavigation())}
            className="inline-flex items-center text-gray-600 hover:text-indigo-600 transition-colors duration-200 group mb-3"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2 group-hover:-translate-x-1 transition-transform duration-200" />
            <span className="font-medium">ກັບຄືນ</span>
          </button>
        </div>

        {/* Video Player */}
        <div className="bg-black rounded-lg sm:rounded-xl overflow-hidden shadow-md sm:shadow-lg mb-4 sm:mb-6 relative">
          {/* Completion Badge */}
          {hasCompleted && (
            <div className="absolute top-2 right-2 z-10">
              <div className="bg-green-500 text-white px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-semibold flex items-center shadow-lg">
                <CheckCircleIcon className="h-3 w-3 mr-1" />
                ສຳເລັດແລ້ວ
              </div>
            </div>
          )}
          <div className="relative aspect-video">
            <ReactPlayer
              ref={playerRef}
              url={streamUrl}
              playing={isPlaying}
              controls
              width="100%"
              height="100%"
              className="absolute inset-0"
              onProgress={handleProgress}
              onEnded={handleEnded}
              onDuration={handleDuration}
              onPlay={() => {
                setIsPlaying(true);
                handleVideoStart();
              }}
              onPause={() => setIsPlaying(false)}
              onReady={() => {
                if (playerRef.current) {
                  const actualDuration = playerRef.current.getDuration();
                  if (actualDuration && actualDuration > 0) {
                    setDuration(actualDuration);
                    setMetadataLoaded(true);
                  }
                }
              }}
              progressInterval={1000}
              config={{
                file: {
                  attributes: {
                    controlsList: "nodownload",
                  },
                  forceVideo: true,
                  forceAudio: false,
                },
              }}
            />
          </div>
        </div>
        {/* Progress Bar */}
        <div className="bg-white rounded-lg sm:rounded-xl shadow-sm sm:shadow-md p-3 sm:p-5 mb-4 sm:mb-6">
          <div className="flex justify-between items-center mb-2 sm:mb-3">
            <span className="flex items-center text-sm sm:text-lg font-semibold text-gray-900">
              <SparklesIcon className="h-5 w-5 mr-2 text-indigo-600" />
              ຄວາມຄືບໜ້າ
            </span>
            <div className="flex items-center space-x-2">
              {hasCompleted && (
                <div className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium flex items-center">
                  <CheckCircleIcon className="h-3 w-3 mr-1" />
                  ສຳເລັດແລ້ວ
                </div>
              )}
              <span className="text-sm sm:text-lg font-semibold text-indigo-600">
                {Math.round(progress)}%
              </span>
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5 sm:h-2">
            <div
              className={`h-full rounded-full transition-all duration-300 ${
                hasCompleted
                  ? "bg-gradient-to-r from-green-500 to-green-600"
                  : "bg-gradient-to-r from-indigo-500 to-purple-600"
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between text-xs sm:text-sm text-gray-500 mt-1.5 sm:mt-2">
            <span>
              ເບິ່ງແລ້ວ: {videoService.formatDuration(watchedDuration)}
            </span>
            <span>ທັງໝົດ: {videoService.formatDuration(duration)}</span>
          </div>
          {hasCompleted && (
            <div className="mt-2 sm:mt-3 p-2 sm:p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center text-green-800">
                <CheckCircleIcon className="h-4 w-4 mr-2" />
                <span className="text-xs sm:text-sm font-medium">
                  ທ່ານໄດ້ສຳເລັດການຮຽນບົດຮຽນນີ້ແລ້ວ!
                </span>
              </div>
            </div>
          )}
        </div>
        <div className="bg-white rounded-lg sm:rounded-xl p-4 sm:p-6 shadow-sm sm:shadow-md mb-4 sm:mb-6 border border-gray-100 text-left">
          <div className="flex flex-col items-start justify-start">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-indigo-600 mb-3 sm:mb-4">
              {video.title}
            </h1>
            <div className="flex flex-wrap items-center justify-start gap-x-3 gap-y-2 text-xs sm:text-base text-gray-500">
              <span className="flex items-center">
                <ClockIcon className="h-4 w-4 mr-1.5" />
                {metadataLoaded
                  ? videoService.formatDuration(duration)
                  : duration > 0
                    ? videoService.formatDuration(duration) + " (ປະມານ)"
                    : "ກຳລັງໂຫຼດ..."}
              </span>
              <span className="hidden sm:inline">•</span>
              <span className="hidden sm:inline">
                {video.views_count || 0} ຄັ້ງທີ່ເບິ່ງ
              </span>
              {hasCompleted && (
                <>
                  <span className="hidden sm:inline">•</span>
                  <span className="hidden xs:flex items-center text-green-600">
                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                    ສຳເລັດແລ້ວ
                  </span>
                </>
              )}
              {!hasCompleted && progress >= 90 && (
                <>
                  <span className="hidden sm:inline">•</span>
                  <span className="hidden xs:flex items-center text-yellow-600">
                    <ClockIcon className="h-4 w-4 mr-2" />
                    ໃກ້ສຳເລັດ
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        {/* Video Description */}
        {video.description && (
          <div className="bg-white rounded-lg sm:rounded-xl shadow-sm sm:shadow-md p-4 sm:p-6 text-left">
            <h2 className="text-xl sm:text-2xl font-semibold text-indigo-600 mb-3 sm:mb-5 flex items-center justify-start">
              <BookOpenIcon className="h-6 w-6 mr-3 text-indigo-600" />
              ກ່ຽວກັບວິດີໂອນີ້
            </h2>
            <div className="bg-gray-50 rounded-xl p-4 sm:p-6">
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap text-base sm:text-lg text-left">
                {video.description}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoPlayer;
